/**
 * InviteRegisterPage — /invite-register?token=X  (INV-2c)
 *
 * Cadastro dedicado de CONVIDADO (staff/atleta convidado por um clube).
 * NÃO é o funil B2B: nunca envia intent='club'/userType, nunca cria
 * organização, nunca navega para /manager/setup.
 *
 * Fluxo:
 * 1. GET invite-info (público) → só renderiza o formulário se status='pending'
 * 2. Submit → POST /auth/register com invite_token
 *    - backend valida token+email ANTES do INSERT (convite inválido = zero conta)
 *    - conta nasce pending + JWT excepcional na resposta
 * 3. 201 → volta para /accept-invite?token=X (aceite ativa a conta)
 *
 * LGPD (contrato real do backend, routes_auth.py):
 * - role ATLETA → coleta birthDate; se <18, researchConsent é obrigatório
 *   (backend: age<18 && !research_consent → 403 LGPD_MINOR_CONSENT_REQUIRED)
 * - roles profissionais → NÃO coleta nascimento (minimização de dados)
 * - Termos/privacidade: validação exclusivamente de UI (backend não persiste)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AZURE } from '../services/api';
import { VUKZ } from '../theme/vukzTheme';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertTriangle, Loader2, Users, Shield, Target, Building2, UserPlus,
  Eye, EyeOff,
} from 'lucide-react';

const ROLE_LABELS = {
  GESTOR: 'Gestor',
  TREINADOR: 'Treinador',
  PREPARADOR_FISICO: 'Preparador Físico',
  ATLETA: 'Atleta',
  RECRUITER: 'Recrutador',
};

const ROLE_ICONS = {
  GESTOR: Shield,
  TREINADOR: Target,
  PREPARADOR_FISICO: Target,
  ATLETA: Users,
  RECRUITER: Users,
};

const STATUS_LABELS = {
  accepted: 'Este convite já foi aceito.',
  cancelled: 'Este convite foi cancelado.',
  expired: 'Este convite expirou. Peça um novo convite ao clube.',
};

function calcAge(birthDateStr) {
  if (!birthDateStr) return null;

  // Parsing local explícito — new Date('YYYY-MM-DD') interpretaria como UTC
  // e em UTC-3 poderia deslocar um dia (crítico na fronteira dos 18 anos).
  const parts = birthDateStr.split('-').map(Number);
  if (parts.length !== 3) return null;

  const [year, month, day] = parts;
  if (!year || !month || !day) return null;

  const birth = new Date(year, month - 1, day);

  // Rejeita datas impossíveis, ex. 2020-02-31 (Date faria rollover silencioso).
  if (
    birth.getFullYear() !== year ||
    birth.getMonth() !== month - 1 ||
    birth.getDate() !== day
  ) {
    return null;
  }

  const today = new Date();

  let age = today.getFullYear() - year;
  if (
    today.getMonth() < month - 1 ||
    (today.getMonth() === month - 1 && today.getDate() < day)
  ) {
    age -= 1;
  }

  return age;
}

export default function InviteRegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, loading: authLoading } = useAuth();
  const token = searchParams.get('token') || '';

  const [inviteInfo, setInviteInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [infoError, setInfoError] = useState(null);

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    birthDate: '',
    acceptTerms: false,
    researchConsent: false,
  });

  const isAthlete = inviteInfo?.role === 'ATLETA';
  const age = isAthlete ? calcAge(form.birthDate) : null;
  const isMinor = age !== null && age < 18;

  // 1. invite-info (público)
  useEffect(() => {
    if (!token) {
      setInfoError('Link de convite inválido — token ausente.');
      setLoadingInfo(false);
      return;
    }
    const fetchInfo = async () => {
      try {
        const { data } = await AZURE.get('/onboarding/invite-info', { params: { token } });
        if (data.success) {
          setInviteInfo(data.data);
          // Pré-preenche o nome com o cadastro do clube (editável)
          if (data.data.invite_name) {
            setForm((prev) => ({ ...prev, fullName: data.data.invite_name }));
          }
        } else {
          setInfoError(data.error || 'Convite não encontrado.');
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404) setInfoError('Convite não encontrado ou link inválido.');
        else if (status === 410) setInfoError('Este convite expirou.');
        else setInfoError('Erro ao carregar informações do convite.');
      } finally {
        setLoadingInfo(false);
      }
    };
    fetchInfo();
  }, [token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!form.fullName.trim() || form.fullName.trim().length < 2) {
      next.fullName = 'Nome é obrigatório';
    }
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) {
      next.email = 'E-mail válido é obrigatório';
    }
    if (!form.password || form.password.length < 6) {
      next.password = 'Senha deve ter pelo menos 6 caracteres';
    }
    if (form.password !== form.confirmPassword) {
      next.confirmPassword = 'As senhas não coincidem';
    }
    if (isAthlete && !form.birthDate) {
      next.birthDate = 'Data de nascimento é obrigatória para atletas';
    } else if (isAthlete && (age === null || age < 0)) {
      // Fail-closed: data inválida NÃO pode passar como adulto (isMinor=false).
      next.birthDate = 'Informe uma data de nascimento válida';
    }
    if (isAthlete && isMinor && !form.researchConsent) {
      next.researchConsent = 'O consentimento é obrigatório para menores de 18 anos';
    }
    if (!form.acceptTerms) {
      next.acceptTerms = 'Você precisa aceitar os termos de uso';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Payload do CONVIDADO — sem intent/userType/organização; a role vem do
    // convite (backend). Campos LGPD só no fluxo de atleta.
    const payload = {
      name: form.fullName.trim(),
      email: form.email.trim(),
      password: form.password,
      invite_token: token,
      ...(isAthlete
        ? {
            birthDate: form.birthDate,
            researchConsent: isMinor ? form.researchConsent : false,
          }
        : {}),
    };

    try {
      const result = await register(payload);
      if (result.success) {
        // Conta pending + JWT excepcional já no AuthContext →
        // volta ao convite para o aceite (que ativa a conta).
        navigate(`/accept-invite?token=${encodeURIComponent(token)}`, { replace: true });
      } else {
        setErrors({ general: result.error || 'Erro ao criar conta. Tente novamente.' });
      }
    } catch (err) {
      console.error('[InviteRegister] Exceção:', err);
      setErrors({ general: 'Erro inesperado. Tente novamente.' });
    }
  };

  const RoleIcon = inviteInfo ? (ROLE_ICONS[inviteInfo.role] || Users) : Users;
  const roleLabel = inviteInfo ? (ROLE_LABELS[inviteInfo.role] || inviteInfo.role) : '';
  const inviteUsable = inviteInfo?.status === 'pending';

  const inputStyle = (field) => ({
    width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 14,
    border: `1px solid ${errors[field] ? VUKZ.error : VUKZ.border}`,
    outline: 'none', background: '#fff',
  });

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#fafafa', padding: 24,
    }}>
      <div style={{
        width: '100%', maxWidth: 460, background: '#fff', borderRadius: 16,
        border: `1px solid ${VUKZ.border}`, overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ background: VUKZ.text, padding: '24px 24px 20px', color: '#fff', textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14, background: 'rgba(255,255,255,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px',
          }}>
            {inviteInfo?.logo_url ? (
              <img src={inviteInfo.logo_url} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }}
                   onError={(e) => { e.target.style.display = 'none'; }} />
            ) : (
              <Building2 size={28} />
            )}
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px' }}>Crie sua conta na VUKZ AI</h1>
          {inviteInfo?.institution_name && (
            <p style={{ fontSize: 14, opacity: 0.85, margin: 0 }}>{inviteInfo.institution_name}</p>
          )}
        </div>

        <div style={{ padding: 24 }}>
          {/* Loading */}
          {loadingInfo && (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <Loader2 size={32} color={VUKZ.textLight} className="animate-spin" style={{ margin: '0 auto 12px' }} />
              <p style={{ color: VUKZ.textLight, fontSize: 14 }}>Carregando convite...</p>
            </div>
          )}

          {/* Convite inválido / não-pending: mostra estado, NUNCA formulário */}
          {!loadingInfo && (infoError || (inviteInfo && !inviteUsable)) && (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <AlertTriangle size={32} color={VUKZ.error} style={{ margin: '0 auto 12px' }} />
              <p style={{ color: VUKZ.error, fontSize: 14, fontWeight: 500 }}>
                {infoError || STATUS_LABELS[inviteInfo?.status] || `Convite ${inviteInfo?.status}.`}
              </p>
              <Link to="/" style={{
                display: 'inline-block', marginTop: 16, padding: '10px 24px', borderRadius: 8,
                background: VUKZ.text, color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none',
              }}>
                Ir para o início
              </Link>
            </div>
          )}

          {/* Formulário — SOMENTE convite pending */}
          {!loadingInfo && !infoError && inviteUsable && (
            <form onSubmit={handleSubmit}>
              {/* Card do convite */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
                padding: '14px 16px', background: '#f3f4f6', borderRadius: 10,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, background: '#e5e7eb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <RoleIcon size={20} color={VUKZ.text} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: VUKZ.text }}>
                    {inviteInfo.invite_name ? `${inviteInfo.invite_name} · ${roleLabel}` : roleLabel}
                  </div>
                  <div style={{ fontSize: 12, color: VUKZ.textLight }}>
                    Papel atribuído pelo convite
                    {inviteInfo.expires_at && (
                      <> · válido até {new Date(inviteInfo.expires_at).toLocaleDateString('pt-BR')}</>
                    )}
                  </div>
                </div>
              </div>

              {errors.general && (
                <div style={{
                  padding: '10px 14px', borderRadius: 8, marginBottom: 16,
                  background: '#fef2f2', border: '1px solid #fecaca',
                  color: VUKZ.error, fontSize: 13,
                }}>
                  {errors.general}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Nome */}
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: VUKZ.text, marginBottom: 4 }}>
                    Nome completo
                  </label>
                  <input name="fullName" type="text" value={form.fullName}
                         onChange={handleChange} style={inputStyle('fullName')} />
                  {errors.fullName && <p style={{ color: VUKZ.error, fontSize: 12, margin: '4px 0 0' }}>{errors.fullName}</p>}
                </div>

                {/* Email */}
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: VUKZ.text, marginBottom: 4 }}>
                    E-mail
                  </label>
                  <input name="email" type="email" autoComplete="email" value={form.email}
                         onChange={handleChange} style={inputStyle('email')}
                         placeholder="Use o e-mail em que recebeu o convite" />
                  {errors.email && <p style={{ color: VUKZ.error, fontSize: 12, margin: '4px 0 0' }}>{errors.email}</p>}
                </div>

                {/* Senha */}
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: VUKZ.text, marginBottom: 4 }}>
                    Senha
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input name="password" type={showPassword ? 'text' : 'password'}
                           autoComplete="new-password" value={form.password}
                           onChange={handleChange} style={inputStyle('password')}
                           placeholder="Mínimo 6 caracteres" />
                    <button type="button" onClick={() => setShowPassword((v) => !v)}
                            style={{
                              position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                              background: 'none', border: 'none', cursor: 'pointer', color: VUKZ.textLight,
                            }}>
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && <p style={{ color: VUKZ.error, fontSize: 12, margin: '4px 0 0' }}>{errors.password}</p>}
                </div>

                {/* Confirmar senha */}
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: VUKZ.text, marginBottom: 4 }}>
                    Confirmar senha
                  </label>
                  <input name="confirmPassword" type={showPassword ? 'text' : 'password'}
                         autoComplete="new-password" value={form.confirmPassword}
                         onChange={handleChange} style={inputStyle('confirmPassword')} />
                  {errors.confirmPassword && <p style={{ color: VUKZ.error, fontSize: 12, margin: '4px 0 0' }}>{errors.confirmPassword}</p>}
                </div>

                {/* Data de nascimento — SOMENTE atleta (minimização de dados) */}
                {isAthlete && (
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: VUKZ.text, marginBottom: 4 }}>
                      Data de nascimento
                    </label>
                    <input name="birthDate" type="date" value={form.birthDate}
                           onChange={handleChange} style={inputStyle('birthDate')}
                           max={new Date().toISOString().slice(0, 10)} />
                    {errors.birthDate && <p style={{ color: VUKZ.error, fontSize: 12, margin: '4px 0 0' }}>{errors.birthDate}</p>}
                  </div>
                )}

                {/* Menor de 18 — consentimento LGPD (contrato real: researchConsent) */}
                {isAthlete && isMinor && (
                  <div style={{
                    padding: '14px 16px', borderRadius: 10,
                    background: '#fefce8', border: '1px solid #fde047',
                  }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#854d0e', margin: '0 0 6px' }}>
                      Cadastro de menor de idade ({age} anos)
                    </p>
                    <p style={{ fontSize: 12, color: '#854d0e', margin: '0 0 10px', lineHeight: 1.5 }}>
                      Pela LGPD (Art. 14), o cadastro de menores de 18 anos exige
                      consentimento de um responsável legal. Ao marcar abaixo, você
                      declara que possui esse consentimento para este cadastro.
                    </p>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                      <input name="researchConsent" type="checkbox" checked={form.researchConsent}
                             onChange={handleChange} style={{ marginTop: 2 }} />
                      <span style={{ fontSize: 12, color: '#854d0e' }}>
                        Confirmo que possuo o consentimento do responsável legal para este cadastro
                      </span>
                    </label>
                    {errors.researchConsent && (
                      <p style={{ color: VUKZ.error, fontSize: 12, margin: '6px 0 0' }}>{errors.researchConsent}</p>
                    )}
                  </div>
                )}

                {/* Termos — validação de UI (separado do consentimento de menor) */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                    <input name="acceptTerms" type="checkbox" checked={form.acceptTerms}
                           onChange={handleChange} style={{ marginTop: 2 }} />
                    <span style={{ fontSize: 13, color: VUKZ.textLight }}>
                      Li e aceito os{' '}
                      <Link to="/termos" target="_blank" style={{ color: VUKZ.text, fontWeight: 600 }}>termos de uso</Link>
                      {' '}e a{' '}
                      <Link to="/privacidade" target="_blank" style={{ color: VUKZ.text, fontWeight: 600 }}>política de privacidade</Link>
                    </span>
                  </label>
                  {errors.acceptTerms && <p style={{ color: VUKZ.error, fontSize: 12, margin: '4px 0 0' }}>{errors.acceptTerms}</p>}
                </div>

                <button type="submit" disabled={authLoading}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                          padding: '12px 20px', borderRadius: 10, background: VUKZ.text,
                          color: '#fff', fontSize: 14, fontWeight: 600, border: 'none',
                          cursor: authLoading ? 'wait' : 'pointer', opacity: authLoading ? 0.7 : 1,
                        }}>
                  {authLoading
                    ? (<><Loader2 size={18} className="animate-spin" /> Criando conta...</>)
                    : (<><UserPlus size={18} /> Criar conta</>)}
                </button>

                <p style={{ fontSize: 13, color: VUKZ.textLight, textAlign: 'center', margin: 0 }}>
                  Já tem uma conta?{' '}
                  <Link
                    to={`/login?redirect=${encodeURIComponent(`/accept-invite?token=${token}`)}&invite_token=${encodeURIComponent(token)}`}
                    style={{ color: VUKZ.text, fontWeight: 600 }}
                  >
                    Entrar
                  </Link>
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
